import { useEffect } from 'react'

import { Button, Input,Label } from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import {useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { z } from 'zod'

import { useVehiclesStore } from '@/store/vehicles.store.ts'

const vehicleSchema = z.object({
  plate: z.string().min(3, 'Минимум 3 символа'),
  brand: z.string().min(2, 'Минимум 2 символа'),
  year: z.number({ error: 'Укажите год' }).int().min(1990, 'Минимум 1990').max(2030, 'Максимум 2030'),
  // assignedDriverId: z.string().optional(),
})

type FormValues = z.infer<typeof vehicleSchema>;

interface VehicleFormProps {
  vehicleId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function VehicleForm({ vehicleId: propId, onSuccess, onCancel }: VehicleFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: routeId } = useParams<{ id: string }>()
  const id = propId || routeId
  const isEdit = Boolean(id)

  const { vehicles, addVehicle, updateVehicle, isLoading } = useVehiclesStore()

  const existing = id ? vehicles.find(v => v.id === id) : undefined

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: existing
      ? {
        plate: existing.plate,
        brand: existing.brand,
        year: existing.year,
        // assignedDriverId: existing.assignedDriverId ?? '',
      }
      : { plate: '', brand: '', year: new Date().getFullYear() /*, assignedDriverId: '' */ },
  })

  useEffect(() => {
    if (existing) {
      reset({
        plate: existing.plate,
        brand: existing.brand,
        year: existing.year,
      })
    } else {
      reset({ plate: '', brand: '', year: new Date().getFullYear() })
    }
  }, [existing, reset])

  const onSubmit = async (data: FormValues) => {
    const payload = {
      ...data,
      // assignedDriverId: data.assignedDriverId || undefined,
      active: true,
    }

    try {
      if (isEdit && existing) {
        await updateVehicle(existing.id, payload)
      } else {
        await addVehicle(payload)
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/vehicles')
      }
    } catch (err) {
      console.error('Failed to save vehicle:', err)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/vehicles')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-1">
      <div className="flex flex-col gap-1">
        <Label htmlFor="vehicle-plate" isRequired>{t('vehicles.plate')}</Label>
        <Input
          id="vehicle-plate"
          placeholder="LB0306А"
          className={errors.plate ? 'border-danger' : ''}
          {...register('plate')}
          variant={'secondary'}
        />
        {errors.plate?.message && <span className="text-xs text-danger">{errors.plate.message}</span>}
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="vehicle-brand" isRequired>{t('vehicles.brand')}</Label>
        <Input
          id="vehicle-brand"
          placeholder="Volvo FH 500"
          className={errors.brand ? 'border-danger' : ''}
          {...register('brand')}
          variant={'secondary'}
        />
        {errors.brand?.message && <span className="text-xs text-danger">{errors.brand.message}</span>}
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="vehicle-year" isRequired>{t('vehicles.year')}</Label>
        <Input
          id="vehicle-year"
          type="number"
          className={errors.year ? 'border-danger' : ''}
          {...register('year', { valueAsNumber: true })}
          variant={'secondary'}
        />
        {errors.year?.message && <span className="text-xs text-danger">{errors.year.message}</span>}
      </div>

      {/* <Controller
        name="assignedDriverId"
        control={control}
        render={({ field }) => (
          <div className="flex flex-col gap-1">
            <Label>{t('reports.driver')}</Label>
            <Select
              className="w-full"
              selectedKeys={field.value ? [field.value] : []}
              onSelectionChange={keys => field.onChange(Array.from(keys)[0])}
            >
              <Select.Trigger
                className="flex items-center justify-between w-full px-3 py-2 border rounded-xl bg-background hover:bg-default-100 transition-colors"
              >
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover className="min-w-[200px] p-1 bg-background border rounded-xl shadow-lg">
                <ListBox>
                  <ListBox.Item id="" textValue={t('common.not_assigned')}>
                    {t('common.not_assigned')}
                  </ListBox.Item>
                  {drivers.map(d => (
                    <ListBox.Item key={d.id} id={d.id} textValue={d.fullName}>
                      {d.fullName}
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
            </Select>
          </div>
        )}
      /> */}

      <div className="flex gap-3 pt-4 justify-end">
        <Button variant="ghost" onClick={handleCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" isDisabled={isLoading}>
          {isLoading ? (
            <div className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isEdit ? t('common.save') : t('vehicles.add_vehicle')}
            </div>
          ) : (
            isEdit ? t('common.save') : t('vehicles.add_vehicle')
          )}
        </Button>
      </div>
    </form>
  )
}
